// FILE: localExecBridge.ts
// Purpose: Carry local-command requests between the server push channels and the desktop bridge.
// Layer: Web transport wiring
// Exports: install/announce/accept/revoke helpers around `window.desktopBridge.localExec`

import {
  WS_CHANNELS,
  WS_METHODS,
  type DesktopBridge,
  type LocalExecAcceptResult,
  type LocalExecAcceptedEntry,
  type LocalExecControlPayload,
  type LocalExecOutcome,
  type LocalExecRequestPayload,
  type ProjectLocalAction,
} from "@taskin-code/contracts";

import type { WsTransport } from "./wsTransport";

type LocalExecDesktopBridge = NonNullable<DesktopBridge["localExec"]>;

/**
 * The transport this tab announces on. Module-scoped because the announce is
 * also triggered from the settings UI after an acceptance changes, long after
 * the bridge was installed.
 */
let activeTransport: WsTransport | null = null;

/**
 * The desktop side of the feature, or null in a plain browser *and* on a
 * desktop build older than this feature — `localExec` is optional in
 * `DesktopBridge` precisely so an old shell degrades to "no device answered"
 * instead of throwing on every push.
 */
export function readLocalExecBridge(): LocalExecDesktopBridge | null {
  if (typeof window === "undefined") return null;
  return window.desktopBridge?.localExec ?? null;
}

/** True when this client can actually run local commands for the user. */
export function hasLocalExecBridge(): boolean {
  return readLocalExecBridge() !== null;
}

/**
 * An outcome for a failure with no process behind it. The relay is waiting on
 * this socket, so a bridge that throws must still produce an answer — the
 * alternative is the agent blocking until the relay's grace period expires.
 */
export function localExecBridgeFailureOutcome(message: string): LocalExecOutcome {
  return {
    status: "error",
    exitCode: null,
    stdout: "",
    stderr: "",
    truncated: false,
    pid: null,
    deviceName: null,
    message,
    uploads: [],
  };
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

async function handleLocalExecRequest(
  payload: LocalExecRequestPayload,
  transport: WsTransport,
): Promise<void> {
  const bridge = readLocalExecBridge();
  if (!bridge) {
    // The relay only pushes to sockets that announced themselves, so this is a
    // bridge that vanished between the announce and the request.
    return;
  }

  let outcome: LocalExecOutcome;
  try {
    outcome = await bridge.run(payload);
  } catch (error) {
    outcome = localExecBridgeFailureOutcome(
      `El escritorio no pudo ejecutar el comando: ${errorMessage(error)}`,
    );
  }

  try {
    await transport.request(WS_METHODS.localExecRespond, {
      requestId: payload.requestId,
      outcome,
    });
  } catch {
    // The relay already gave up on this request, or the socket is gone.
  }
}

async function handleLocalExecControl(payload: LocalExecControlPayload): Promise<void> {
  const bridge = readLocalExecBridge();
  if (!bridge) return;
  try {
    if (payload.kind === "stop-thread") {
      await bridge.stopThread(payload);
    } else {
      await bridge.stopProcess(payload);
    }
  } catch {
    // Stopping is fire-and-forget by design: the thread may already be gone.
  }
}

/**
 * Tell the server this socket is a desktop, on this machine, holding these
 * acceptances.
 *
 * Safe to call at any time: it does nothing without a bridge, and nothing while
 * the socket is not open — the state listener re-announces on every (re)connect,
 * which is also what makes an announce lost to a dropped connection harmless.
 */
export async function announceLocalExecDevice(): Promise<void> {
  const bridge = readLocalExecBridge();
  const transport = activeTransport;
  if (!bridge || !transport || transport.getState() !== "open") return;

  try {
    const [device, accepted] = await Promise.all([bridge.getDeviceInfo(), bridge.listAccepted()]);
    await transport.request(WS_METHODS.localExecAnnounce, {
      deviceId: device.deviceId,
      deviceName: device.deviceName,
      accepted,
    });
  } catch {
    // Announcing again is cheap and happens on the next reconnect anyway.
  }
}

/** Read this device's acceptances; empty in a plain browser. */
export async function listAcceptedLocalActions(): Promise<ReadonlyArray<LocalExecAcceptedEntry>> {
  const bridge = readLocalExecBridge();
  if (!bridge) return [];
  return bridge.listAccepted();
}

/**
 * Ask the desktop to accept `entry` on this machine. The dialog and the
 * decision live in the main process — this only carries the request and
 * re-announces the resulting set.
 */
export async function acceptLocalAction(entry: ProjectLocalAction): Promise<LocalExecAcceptResult> {
  const bridge = readLocalExecBridge();
  if (!bridge) return { accepted: false, entries: [] };
  const result = await bridge.accept(entry);
  await announceLocalExecDevice();
  return result;
}

export async function revokeLocalAction(
  entryId: string,
): Promise<ReadonlyArray<LocalExecAcceptedEntry>> {
  const bridge = readLocalExecBridge();
  if (!bridge) return [];
  const entries = await bridge.revoke(entryId);
  await announceLocalExecDevice();
  return entries;
}

/**
 * Subscribe the two local-exec push channels and keep the announce in sync with
 * the connection. Called once, from `createWsNativeApi`.
 */
export function installLocalExecBridge(transport: WsTransport): void {
  activeTransport = transport;

  transport.subscribe(WS_CHANNELS.localExecRequest, (message) => {
    void handleLocalExecRequest(message.data, transport);
  });
  transport.subscribe(WS_CHANNELS.localExecControl, (message) => {
    void handleLocalExecControl(message.data);
  });
  transport.onStateChange((state) => {
    if (state === "open") {
      void announceLocalExecDevice();
    }
  });
}
